/**
 * Fetches products from a specific Shopify collection using the Storefront API with pagination
 * @param {string} storefrontAccessToken - Your Storefront API access token
 * @param {string} shopDomain - Your shop domain without the protocol (e.g., 'your-store.myshopify.com')
 * @param {string} collectionId - The ID or handle of the collection to fetch products from
 * @param {number} [maxProducts=1500] - Maximum number of products to fetch
 * @param {number} [pageSize=250] - Number of products to fetch per request (max 250)
 * @returns {Promise<Array>} - Promise resolving to an array of products from the collection
 */
const getAllCollectionProducts = async (
  storefrontAccessToken,
  shopDomain,
  collectionId,
  maxProducts = 1500,
  pageSize = 250
) => {
  if (!storefrontAccessToken || !shopDomain || !collectionId) {
    throw new Error("Storefront access token, shop domain, and collection ID are required");
  }

  // Limit pageSize to 250 (Shopify's max)
  const actualPageSize = Math.min(pageSize, 250);
  let allProducts = [];
  let hasNextPage = true;
  let cursor = null;

  try {
    // Ensure the shop domain has a protocol and format it properly
    const formattedShopDomain = shopDomain.includes("://")
      ? shopDomain
      : `https://${shopDomain}`;

    console.log("Using shop domain:", formattedShopDomain);
    console.log("Fetching products from collection:", collectionId);

    // Calculate how many pages we need to fetch
    const pagesToFetch = Math.ceil(maxProducts / actualPageSize);
    console.log(
      `Will fetch up to ${pagesToFetch} pages of ${actualPageSize} products each`
    );

    // API URL
    const apiUrl = `${formattedShopDomain}/api/2024-07/graphql.json`;
    console.log("Fetching from API URL:", apiUrl);

    // Determine if collectionId is a handle or ID
    const isHandle = !collectionId.includes('/') && !collectionId.startsWith('gid://');
    
    // Fetch products page by page
    for (
      let page = 0;
      page < pagesToFetch && hasNextPage && allProducts.length < maxProducts;
      page++
    ) {
      console.log(
        `Fetching page ${page + 1} of products from collection${
          cursor ? " after cursor: " + cursor : ""
        }`
      );

      try {
        const currentPageProducts = await fetchCollectionProductsPage(
          apiUrl,
          storefrontAccessToken,
          collectionId,
          isHandle,
          actualPageSize,
          cursor
        );

        if (
          !currentPageProducts ||
          !currentPageProducts.products ||
          currentPageProducts.products.length === 0
        ) {
          console.log("No more products to fetch");
          hasNextPage = false;
          break;
        }

        // Update cursor for next page if there is one
        const pageInfo = currentPageProducts.pageInfo;
        hasNextPage = pageInfo.hasNextPage;
        cursor = pageInfo.endCursor;

        // Process and add products to our collection
        const processedProducts = processProducts(currentPageProducts.products);
        allProducts = [...allProducts, ...processedProducts];

        console.log(
          `Fetched ${processedProducts.length} products, total so far: ${allProducts.length}`
        );

        // If we got fewer products than requested, there are no more to fetch
        if (processedProducts.length < actualPageSize) {
          hasNextPage = false;
        }
      } catch (pageError) {
        console.error(`Error fetching page ${page + 1}:`, pageError);
        // Continue with next page if there's an error
        continue;
      }
    }

    console.log(`Finished fetching products from collection. Total: ${allProducts.length}`);
    return allProducts;
  } catch (error) {
    console.error("Error fetching products from collection:", error);
    throw error;
  }
};

/**
 * Fetches a single page of products from a collection
 * @param {string} apiUrl - Shopify API URL
 * @param {string} token - Access token
 * @param {string} collectionId - Collection ID or handle
 * @param {boolean} isHandle - Whether collectionId is a handle or ID
 * @param {number} pageSize - Products per page
 * @param {string} cursor - Pagination cursor
 * @returns {Promise<Object>} - Products and pagination info
 */
async function fetchCollectionProductsPage(
  apiUrl,
  token,
  collectionId,
  isHandle,
  pageSize,
  cursor = null
) {
  // Define the GraphQL query with pagination - optimized for collection products
  const collectionQuery = isHandle 
    ? `
      query getCollectionProductsByHandle($handle: String!, $first: Int!, $after: String) {
        collection(handle: $handle) {
          products(first: $first, after: $after) {
            pageInfo {
              hasNextPage
              endCursor
            }
            edges {
              cursor
              node {
                id
                title
                handle
                metafield(key: "color-pattern", namespace: "shopify") {
                  key
                  id 
                  value
                  type
                  references(first: 10) {
                    edges {
                      node {
                        ... on Metaobject {
                          id
                          handle
                          fields {
                            key
                            value
                          }
                        }
                      }
                    }
                  }
                }
                variants(first: 100) {
                  edges {
                    node {
                      id
                      title
                      sku
                      price {
                        amount
                        currencyCode
                      }
                      availableForSale
                    }
                  }
                }
              }
            }
          }
        }
      }
    `
    : `
      query getCollectionProductsById($id: ID!, $first: Int!, $after: String) {
        collection(id: $id) {
          products(first: $first, after: $after) {
            pageInfo {
              hasNextPage
              endCursor
            }
            edges {
              cursor
              node {
                id
                title
                handle
                metafield(key: "color-pattern", namespace: "shopify") {
                  key
                  id 
                  value
                  type
                  references(first: 10) {
                    edges {
                      node {
                        ... on Metaobject {
                          id
                          handle
                          fields {
                            key
                            value
                          }
                        }
                      }
                    }
                  }
                }
                variants(first: 100) {
                  edges {
                    node {
                      id
                      title
                      sku
                      price {
                        amount
                        currencyCode
                      }
                      availableForSale
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;

  // Make the request with appropriate variables
  const queryVariables = isHandle 
    ? { handle: collectionId, first: pageSize, after: cursor }
    : { id: collectionId, first: pageSize, after: cursor };

  const response = await fetch(apiUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Shopify-Storefront-Access-Token": token,
    },
    body: JSON.stringify({
      query: collectionQuery,
      variables: queryVariables,
    }),
  });

  if (!response.ok) {
    console.error("HTTP error:", response.status, response.statusText);
    throw new Error(`HTTP error: ${response.status} ${response.statusText}`);
  }

  const result = await response.json();

  if (!result || !result.data) {
    console.error("Response missing data property:", result);

    if (result.errors) {
      console.error("GraphQL errors:", result.errors);
      throw new Error(
        `GraphQL errors: ${result.errors.map((e) => e.message).join(", ")}`
      );
    }

    return null;
  }

  const { data } = result;

  if (!data.collection?.products?.edges) {
    console.log("No products found in this collection page");
    return null;
  }

  return {
    products: data.collection.products.edges,
    pageInfo: data.collection.products.pageInfo,
  };
}

/**
 * Process raw products data into a simplified format
 * @param {Array} productsEdges - Array of product edges from the API
 * @returns {Array} - Processed products
 */
function processProducts(productsEdges) {
  return productsEdges.map((edge) => {
    const product = edge.node;

    // Process the metafield references to extract color information
    let colorData = null;
    if (product.metafield && product.metafield.references) {
      const metaobjects = product.metafield.references.edges.map((edge) => {
        const metaobject = edge.node;
        // Convert fields array to an object for easier access
        const fieldsObject = {};
        if (metaobject.fields) {
          metaobject.fields.forEach((field) => {
            fieldsObject[field.key] = field.value;
          });
        }
        return {
          id: metaobject.id,
          handle: metaobject.handle,
          fields: fieldsObject,
        };
      });

      colorData = {
        rawValue: product.metafield.value,
        type: product.metafield.type,
        metaobjects,
      };
    }

    // Transform nested edges data structure into simpler arrays
    return {
      id: product.id.split("/").pop(), // Convert gid to simple ID
      title: product.title,
      handle: product.handle,
      colorData,
      variants: product.variants.edges.map((variant) => ({
        id: variant.node.id.split("/").pop(),
        title: variant.node.title,
        sku: variant.node.sku,
        price: variant.node.price.amount,
        currencyCode: variant.node.price.currencyCode,
        availableForSale: variant.node.availableForSale,
      })),
    };
  });
}

/**
 * Example usage:
 *
 * getAllCollectionProducts(
 *   'your-storefront-access-token', 
 *   'your-store.myshopify.com',
 *   'collection-handle-or-id'
 * )
 *   .then(products => {
 *     console.log(`Found ${products.length} products in collection`);
 *     // Do something with the products
 *   })
 *   .catch(error => {
 *     console.error('Failed to fetch collection products:', error);
 *   });
 *
 * Note: This function fetches up to 1500 products by default. You can customize
 * the maxProducts parameter to fetch more or fewer products as needed.
 */

export default getAllCollectionProducts; 